import checkboxStyle from '@components/atoms/Checkbox/styles/checkout';
import { Meta } from '@components/atoms/MetaTags/Meta';
import { PAY_OR_GO_SETTINGS } from '@src/constants/pay-or-go';
import {
  CustomerLoginPreDocument,
  GetCustomerDocument,
  GetSubscriptionPageByCompaxIdDocument,
  ProductsOfferingDocument,
  ResendActivationLinkDocument,
} from '@src/lib/graphql/generated';
import { client, ssrAuthQuery, ssrMutation } from '@src/lib/graphql/urql';
import {
  type PaymentMethod,
  resolveSubscriptionCheckoutSettings,
  type SubscriptionCheckoutPage,
} from '@src/utils/subscriptionCheckout';
import { decodeOptions } from '@src/utils/urlHelpers';
import { Elements } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import {
  CheckoutAddressStep,
  type CheckoutAuthFunctions,
  CheckoutCartSummary,
  CheckoutLoginStep,
  CheckoutPaymentStep,
  CheckoutProcessingOverlay,
  CheckoutProvider,
  CheckoutStepIndicator,
  CheckoutSummaryStep,
  Image,
  Link,
  StripePaymentHandler,
  useCheckout,
  useCheckoutCallbacks,
} from '@vgn-medien-holding/vgn-fe-components';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { getSession, signIn, signOut } from 'next-auth/react';
import { twMerge } from 'tailwind-merge';

const Footer = dynamic(() => import('@components/organisms/FooterReduced/Footer').then((mod) => mod.Footer), {
  ssr: true,
});

const STEP_TITLES = ['Login', 'Kontakt', 'Bezahlen', 'Check Out'];

type CheckoutProps = InferGetServerSidePropsType<typeof getServerSideProps>;

function CheckoutInner({
  pricingData,
  slug,
  marketingCode,
  successRedirect,
  paymentMethods,
}: {
  pricingData: CheckoutProps['pricingData'];
  slug: string;
  marketingCode: string;
  successRedirect: string;
  paymentMethods: CheckoutProps['paymentMethods'];
}) {
  const router = useRouter();
  const checkout = useCheckout();

  const {
    handleCheckEmail,
    handleLogin,
    handleRegister,
    handleStripeInit,
    handleSubmit,
    stripePromise,
    isProcessingOrder,
  } = useCheckoutCallbacks({
    portal: 'tvmedia',
    marketingCode,
    offeringId: slug,
    productName: pricingData?.productDisplayName || 'TV-MEDIA Abo',
    productPrice: pricingData?.totalAmount ?? 0,
    successRedirectUrl: successRedirect,
    payOrGoPermission: PAY_OR_GO_SETTINGS.permission,
    auth: { signIn, signOut, getSession } as CheckoutAuthFunctions,
    loginPre: async (email: string) => {
      const { data } = await client.mutation(CustomerLoginPreDocument, { email, password: '' }).toPromise();
      return { error_code: data?.customerLoginPre?.error_code };
    },
    resendActivationLink: async (email: string) => {
      await client.mutation(ResendActivationLinkDocument, { email }).toPromise();
    },
    navigateTo: router.push,
  });

  const isPurAbo = pricingData?.isPurAbo || false;

  const cartBonuses = pricingData?.bonuses
    ?.filter((b: { bonusName?: string | null } | null): b is { bonusName?: string | null } => b !== null)
    .map((b: { bonusName?: string | null }) => ({
      name: b.bonusName || '',
      included: true,
    }));

  const stripeOptions = checkout.stripeState.clientSecret
    ? {
        clientSecret: checkout.stripeState.clientSecret,
        appearance: {
          theme: 'night' as const,
          variables: {
            colorPrimary: '#FF0000',
            colorText: '#FFFFFF',
            colorTextPlaceholder: '#AAAAAA',
            colorBackground: '#000000',
            colorDanger: '#FF0000',
            colorIconCardError: '#FF0000',
            borderRadius: '9999px',
            fontFamily: '"Proxima Nova", sans-serif',
            colorIcon: '#FF0000',
          },
          rules: {
            '.Input': {
              border: '1px solid #AAAAAA',
              backgroundColor: '#000000',
              boxShadow: 'none',
              padding: '10px 16px',
              color: '#FFFFFF',
            },
            '.Input:focus': {
              border: '1px solid #FFFFFF',
              boxShadow: 'none',
            },
            '.Input--invalid': {
              border: '1px solid #FF0000',
              color: '#FF0000',
            },
            '.Label': {
              color: '#AAAAAA',
              fontSize: '14px',
            },
            '.Error': {
              color: '#FF0000',
            },
          },
        },
      }
    : undefined;

  const paymentClassProps = {
    paymentOption:
      'label-base inline-flex items-center gap-2 rounded-full border border-gray-400 px-5 py-3 text-white transition-colors bg-black hover:border-tvmedia hover:bg-white/5',
    paymentOptionActive: 'border-tvmedia bg-white/5',
  };

  const cardElementStyle = {
    base: {
      fontSize: '16px',
      color: '#FFFFFF',
      fontFamily: '"Proxima Nova", sans-serif',
      '::placeholder': { color: '#AAAAAA' },
      iconColor: '#FF0000',
    },
    invalid: { color: '#FF0000', iconColor: '#FF0000' },
  };

  const marketingLabel = (
    <span className="text-gray-400 text-sm leading-relaxed">
      Ich stimme zu, dass die VGN Medien Holding GmbH, die VGN Digital GmbH, die Verlagsgruppe News Medienservice GmbH,
      Woman GmbH &amp; Co KG und Gusto GmbH &amp; Co KG die von mir angegebenen personenbezogenen Daten (Name, Titel,
      Anschrift, E-Mail-Adresse, Telefonnummer, Geburtsdatum, Fotos) zwecks Übermittlung von Informationsmaterial über
      deren Magazine und Online-Medien, für Marketingmaßnahmen (Gewinnspiele, Veranstaltungen, Newsletter für deren
      Waren und Dienstleistungen) sowie für Abo- und Sonderwerbeaktionen, verarbeitet und mich zu den vorgenannten
      Zwecken via SMS, E-Mail, Telefon und Post kontaktiert. Diese Einwilligung kann ich jederzeit unter{' '}
      <a href="mailto:datenschutz@vgn.at" className="text-tvmedia underline">
        datenschutz@vgn.at
      </a>{' '}
      widerrufen. Nähere Informationen zur Verarbeitung meiner personenbezogenen Daten erhalte ich in der{' '}
      <a
        href="https://www.vgn.at/pdf/Datenschutzpolicy.pdf"
        target="_blank"
        rel="noopener noreferrer"
        className="text-tvmedia underline"
      >
        Datenschutzerklärung
      </a>
      .
    </span>
  );

  return (
    <>
      <Head>
        {Meta(
          {
            meta_title: 'Abo Checkout',
            meta_description: 'Abo Checkout',
            no_index: true,
          },
          'Abo Checkout',
        )}
        <link rel="preconnect" href="https://js.stripe.com" data-ot-ignore="true" />
        <link rel="dns-prefetch" href="https://js.stripe.com" data-ot-ignore="true" />
        <script src="https://js.stripe.com/v3/" data-ot-ignore="true" async />
      </Head>
      <header className={twMerge('pointer-events-none fixed z-50 m-auto w-full')}>
        <div className="flex h-16 w-full items-center bg-black px-4 md:px-8">
          <Link href="/" classProps={{ root: 'pointer-events-auto' }}>
            <Image src="/assets/logos/tvm/logo.png" alt="TV-MEDIA Logo" width="130" height="32" copyright="" />
          </Link>
        </div>
      </header>
      <div className="flex min-h-screen flex-col items-center bg-black py-8 pt-24 text-white">
        <div className="flex w-full max-w-7xl flex-col gap-8 p-4 md:flex-row">
          <div className="checkout-form max-w-4xl flex-1">
            <CheckoutStepIndicator
              steps={STEP_TITLES.map((title) => ({ title }))}
              classProps={{
                step: 'flex size-6 rounded-full text-xs font-bold text-black bg-gray-400',
                stepActive: 'bg-white',
                stepCompleted: 'bg-white',
                stepNumber: 'm-auto block',
                line: 'my-auto w-full border-t border-gray-400',
                label: 'label-sm absolute top-7 mt-1 truncate text-center text-xs uppercase text-gray-400',
              }}
            />

            <CheckoutLoginStep
              stepIndex={0}
              onCheckEmail={handleCheckEmail}
              onLogin={handleLogin}
              onRegister={handleRegister}
              title="Nur noch wenige Schritte zu Ihrem Wunschabo."
              description="Bitte E-Mail-Adresse eingeben:"
            />

            <CheckoutAddressStep
              stepIndex={1}
              showPhone
              phoneRequired={false}
              showBirthdate
              showShippingOption={!isPurAbo}
              checkboxClassProps={checkboxStyle}
              dropdownClassProps={{
                root: 'mt-6',
                button:
                  'w-full rounded-full border border-gray-400 bg-black px-4 py-[10.5px] text-left text-sm text-white shadow-none flex justify-between items-center',
                buttonPlaceholder: 'text-gray-400',
                itemWrapper:
                  'z-50 mt-1 max-h-80 overflow-auto rounded-2xl border border-gray-400 bg-gray-800 p-2 shadow-lg',
                item: 'relative cursor-pointer rounded-full px-4 py-2.5 text-sm text-white data-[active]:bg-white/10',
                itemSelected: 'bg-white/10',
                errorMessage: 'ml-5 mt-1 text-xs text-tvmedia',
              }}
            />

            {stripePromise && stripeOptions ? (
              <Elements stripe={stripePromise as Stripe} options={stripeOptions}>
                <StripePaymentHandler />
                <CheckoutPaymentStep
                  stepIndex={2}
                  paymentMethods={isPurAbo ? ['creditcard'] : (paymentMethods ?? ['sepa', 'creditcard', 'invoice'])}
                  onStripeInit={handleStripeInit}
                  stripePromise={stripePromise}
                  stripeClientSecret={checkout.stripeState.clientSecret}
                  classProps={paymentClassProps}
                  cardElementStyle={cardElementStyle}
                />
              </Elements>
            ) : (
              <CheckoutPaymentStep
                stepIndex={2}
                paymentMethods={isPurAbo ? ['creditcard'] : (paymentMethods ?? ['sepa', 'creditcard', 'invoice'])}
                onStripeInit={handleStripeInit}
                classProps={paymentClassProps}
                cardElementStyle={cardElementStyle}
              />
            )}

            <CheckoutSummaryStep
              stepIndex={3}
              onSubmit={handleSubmit}
              agbLink="https://www.vgn.at/agb"
              privacyLink="https://www.vgn.at/pdf/Datenschutzpolicy.pdf"
              marketingLabel={marketingLabel}
              submitButtonLabel="ZAHLUNGSPFLICHTIG BESTELLEN"
              checkboxClassProps={{
                ...checkboxStyle,
                // Force both consent labels to the same size (the package gives the
                // marketing label its own text-sm span while the AGB span has none).
                label: `${checkboxStyle.label ?? ''} [&_span]:[font-size:inherit] [&_span]:[line-height:inherit]`,
              }}
            />
          </div>

          <CheckoutCartSummary
            title="Ihre Auswahl"
            productName={pricingData?.productDisplayName || 'TV-MEDIA Magazin + E-Paper'}
            baseAmount={pricingData?.baseAmount ?? 0}
            deliveryCost={pricingData?.deliveryCost ?? null}
            totalAmount={pricingData?.totalAmount ?? 0}
            bonuses={cartBonuses}
            legalText={pricingData?.legalText ?? undefined}
            legalCancel={pricingData?.legalCancel ?? undefined}
            classProps={{
              root: 'bg-gray-800 text-white border border-tvmedia rounded-2xl p-8 h-fit',
              header: 'text-lg font-bold text-white',
              totalRow: 'font-bold text-white',
              legalText: 'text-xs text-gray-400',
            }}
          />
        </div>

        {isProcessingOrder && (
          <CheckoutProcessingOverlay
            classProps={{
              backdrop: 'fixed inset-0 z-50 flex items-center justify-center bg-black/70',
              card: 'mx-4 max-w-md rounded-2xl border border-tvmedia bg-gray-800 p-8 text-center text-white',
              title: 'mb-2 text-xl font-bold text-white',
            }}
          />
        )}
      </div>
      <Footer />
    </>
  );
}

export default function AboCheckout({
  user,
  pricingData,
  slug,
  marketingCode,
  successRedirect,
  paymentMethods,
}: CheckoutProps) {
  const isPurAbo = pricingData?.isPurAbo || false;

  return (
    <CheckoutProvider
      initialStep={user ? 1 : 0}
      totalSteps={4}
      initialFormData={{
        ...(user
          ? {
              email: user.email || '',
              firstName: user.firstName || '',
              lastName: user.lastName || '',
            }
          : {}),
        ...(isPurAbo ? { payment: { id: 'creditcard', label: 'Kreditkarte' } } : {}),
      }}
    >
      <CheckoutInner
        pricingData={pricingData}
        slug={slug}
        marketingCode={marketingCode}
        successRedirect={successRedirect}
        paymentMethods={paymentMethods}
      />
    </CheckoutProvider>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ params, req }) => {
  // The proxy middleware rewrites the URL for logged-in users, JSON-encoding the
  // auth token + permission into the last path segment (e.g. {"slug":"401018616",
  // "params":{token,permission}}). Only that encoded form must be decoded — a bare
  // numeric offering id like "401018616" is itself valid JSON (a number), so
  // decodeOptions would wrongly parse it and lose the slug. Detect the encoded
  // object explicitly and leave plain ids untouched.
  // Route is /abo/checkout/<offeringId>[/<marketingCode>]; the proxy encodes whichever
  // segment is last, so either one may carry the encoded form.
  const decodeSegment = (raw?: string) =>
    typeof raw === 'string' && raw.trim().startsWith('{')
      ? decodeOptions(raw)
      : { slug: raw ?? '', params: {} as Record<string, unknown> };
  const mcSegments = params?.mc as string[] | undefined;
  if (mcSegments && mcSegments.length > 1) return { notFound: true };
  const decodedId = decodeSegment(params?.id as string);
  const decodedMc = decodeSegment(mcSegments?.[0]);
  const id = decodedId.slug;
  const marketingCode = decodedMc.slug || 'TVMEDIA';
  const encodedParams = { ...decodedId.params, ...decodedMc.params } as { token?: string };
  if (!id) return { notFound: true };

  let user = null;
  try {
    const authToken = encodedParams?.token || req.headers.authorization?.replace('Bearer ', '') || null;
    if (authToken) {
      const { data: userData } = await ssrAuthQuery({
        query: GetCustomerDocument,
        variables: {},
        authToken: authToken,
      });
      user = userData?.customerCurrent || null;
    }
  } catch {
    // Ignore auth errors
  }

  let pricingData = null;

  try {
    const { data: pricingResponse } = await ssrMutation({
      query: ProductsOfferingDocument,
      variables: {
        offeringId: id,
        marketingCode,
      },
    });

    if (pricingResponse?.productsOffering) {
      const offering = pricingResponse.productsOffering;
      const recurringAmount = offering.total?.recurring?.amount || null;
      const oneTimeAmount = offering.total?.oneTime?.amount || 0;
      const totalAmount = (recurringAmount || 0) + oneTimeAmount;

      let deliveryCost = offering.deliveryCost ? parseFloat(String(offering.deliveryCost)) : null;

      const amountNet = offering.total?.oneTime?.amountNet || 0;
      const taxTotal = offering.total?.oneTime?.taxAmounts?.reduce((sum, tax) => sum + (tax?.amount || 0), 0) || 0;
      const baseProductPrice = amountNet + taxTotal;

      if (deliveryCost === null && oneTimeAmount > 0 && baseProductPrice > 0) {
        const calculatedShipping = oneTimeAmount - baseProductPrice;
        if (calculatedShipping > 0 && calculatedShipping < oneTimeAmount) {
          deliveryCost = calculatedShipping;
        }
      }

      const baseAmount = baseProductPrice > 0 ? baseProductPrice : oneTimeAmount - (deliveryCost || 0);

      const offeringName = offering.entries?.[0]?.offeringName || '';
      const isPurAbo = offeringName.toLowerCase().includes('pur-abo');

      const vnFeProductDisplays = offering.vnFeProductDisplays as Array<{ name?: string | null }> | undefined;
      const productDisplayName = vnFeProductDisplays?.[0]?.name || 'TV-MEDIA Magazin + E-Paper';

      pricingData = {
        totalAmount,
        deliveryCost: deliveryCost && deliveryCost > 0 ? deliveryCost : null,
        baseAmount: baseAmount > 0 ? baseAmount : null,
        bonuses: offering.bonuses || [],
        legalText: offering.legal_text || null,
        legalCancel: offering.legal_cancel || null,
        isPurAbo,
        productDisplayName,
      };
    }
  } catch (error) {
    console.error('[Checkout] Error fetching pricing:', error);
    return { notFound: true };
  }

  if (!pricingData) {
    return { notFound: true };
  }

  // Success redirect: CMS-overridable per offering (same mechanism as trend's
  // /pur-abo flow) — falls back to the built-in success page until a
  // SubscriptionPage entry with success_redirect exists for this compax id.
  let successRedirect = '/abo/checkout/success?hidebanner=true';
  try {
    const { data: subPageData } = await client
      .query(GetSubscriptionPageByCompaxIdDocument, { compaxId: id }, { requestPolicy: 'network-only' })
      .toPromise();
    if (subPageData?.subscriptionPageByCompaxId?.success_redirect) {
      successRedirect = subPageData.subscriptionPageByCompaxId.success_redirect;
    }
  } catch {
    // Ignore — default used
  }

  // Dropdown options contain separate Compax ids. Resolve the parent landing-page
  // group so checkout settings configured on its parent (or one member) apply to all.
  let paymentMethods: PaymentMethod[] | null = null;
  try {
    const { data: settingsData } = await client
      .query<{ subscriptionPages?: (SubscriptionCheckoutPage | null)[] | null }>(
        'query GetSubscriptionCheckoutSettings { subscriptionPages(max: 1000) { compax_id payment_options success_redirect multiple_options { option_buy_link } } }',
        {},
        { requestPolicy: 'network-only' },
      )
      .toPromise();
    const settings = resolveSubscriptionCheckoutSettings(
      settingsData?.subscriptionPages?.filter((page): page is SubscriptionCheckoutPage => Boolean(page)) ?? [],
      id,
    );
    paymentMethods = settings.paymentMethods;
    if (settings.successRedirect) successRedirect = settings.successRedirect;
  } catch {
    // Compatibility fallback for middleware versions without the grouped fields.
    try {
      const { data: paymentData } = await client
        .query<{ subscriptionPageByCompaxId?: { payment_options?: (string | null)[] | null } | null }>(
          'query GetSubscriptionPagePaymentOptions($compaxId: String!) { subscriptionPageByCompaxId(compaxId: $compaxId) { payment_options } }',
          { compaxId: id },
          { requestPolicy: 'network-only' },
        )
        .toPromise();
      paymentMethods = resolveSubscriptionCheckoutSettings(
        paymentData?.subscriptionPageByCompaxId ? [paymentData.subscriptionPageByCompaxId] : [],
        id,
      ).paymentMethods;
    } catch {
      // Ignore — all methods allowed.
    }
  }

  return { props: { slug: id, marketingCode, user, pricingData, successRedirect, paymentMethods } };
};
